/**
 * Recency tracking (S3.14 + S3.15). Per-workspace because workflows differ.
 * Stored as `{ commandId: lastUsedTimestamp }`, pruned to the 200 most-recent
 * entries (LRU). Drives both the empty-state "Recent" section and the recency
 * ranking boost — the thing that makes the palette feel telepathic.
 */

import type { Command } from "../types";

const MAX_ENTRIES = 200;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

export type RecencyMap = Record<string, number>;

function storageKey(workspaceId: string | null | undefined): string | null {
  return workspaceId ? `kuvio_command_palette_recency_${workspaceId}` : null;
}

export function loadRecency(workspaceId: string | null | undefined): RecencyMap {
  const key = storageKey(workspaceId);
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as RecencyMap) : {};
  } catch {
    return {};
  }
}

/** Record a command use, pruning to the 200 most-recent ids (LRU). */
export function recordCommandUse(workspaceId: string | null | undefined, commandId: string): void {
  const key = storageKey(workspaceId);
  if (!key) return;
  try {
    const map = loadRecency(workspaceId);
    map[commandId] = Date.now();
    const entries = Object.entries(map);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1] - a[1]);
      const pruned: RecencyMap = {};
      for (const [id, ts] of entries.slice(0, MAX_ENTRIES)) pruned[id] = ts;
      localStorage.setItem(key, JSON.stringify(pruned));
    } else {
      localStorage.setItem(key, JSON.stringify(map));
    }
  } catch {
    /* ignore — recency is best-effort */
  }
}

/** Ranking multiplier: 1.5× if used in last 24h, 1.2× in last 7d, else 1.0×. */
export function recencyBoost(ts: number | undefined, now: number = Date.now()): number {
  if (!ts) return 1;
  const age = now - ts;
  if (age < DAY_MS) return 1.5;
  if (age < WEEK_MS) return 1.2;
  return 1;
}

export function formatRecency(ts: number, now: number = Date.now()): string {
  const age = Math.max(0, now - ts);
  const min = Math.floor(age / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} d ago`;
}

/**
 * The empty-state "Recent" section: the N most-recently-used commands that
 * still exist in the live index, cloned to kind "recent" with a recency
 * subtitle. Stale ids (a deleted agent) simply drop out.
 */
export function selectRecentCommands(all: Command[], recency: RecencyMap, limit: number): Command[] {
  const byId = new Map(all.map((c) => [c.id, c]));
  const now = Date.now();
  return Object.entries(recency)
    .filter(([id]) => byId.has(id))
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([id, ts]) => {
      const cmd = byId.get(id)!;
      return { ...cmd, kind: "recent" as const, subtitle: formatRecency(ts, now) };
    });
}
